import { Link } from "react-router-dom";
import type { Software } from "@/types";
import { formatDownloads, platformLabel } from "@/utils/software";
import RatingStars from "./RatingStars";

interface SoftwareCardProps {
  software: Software;
  index?: number;
}

export default function SoftwareCard({ software, index = 0 }: SoftwareCardProps) {
  return (
    <article
      className="relative group bg-background-50 rounded-lg border border-background-200 p-4 md:p-5 transition-all duration-300 hover:border-primary-300 hover:-translate-y-0.5 animate-fade-up"
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      <Link
        to={`/detail/${software.id}`}
        className="absolute inset-0 z-10 rounded-lg cursor-pointer"
        aria-label={`查看 ${software.name} 详情`}
      ></Link>

      <div className="flex items-start gap-4">
        <div className="w-16 h-16 md:w-[68px] md:h-[68px] rounded-lg overflow-hidden bg-background-100 border border-background-200 shrink-0">
          <img
            src={software.icon}
            alt={`${software.name} 软件图标`}
            title={`${software.name} 手机软件图标`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 truncate">
              {software.name}
            </h3>
            <span className="text-[11px] text-primary-700 bg-primary-100 rounded-full px-2 py-0.5 whitespace-nowrap">
              v{software.version}
            </span>
            {software.price === "free" ? (
              <span className="text-[11px] text-accent-700 bg-accent-100 rounded-full px-2 py-0.5 whitespace-nowrap">免费</span>
            ) : (
              <span className="text-[11px] text-secondary-700 bg-secondary-200 rounded-full px-2 py-0.5 whitespace-nowrap">
                付费 ¥{software.priceValue}
              </span>
            )}
          </div>

          <p className="mt-1.5 text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed">
            {software.shortDesc}
          </p>

          <div className="mt-2.5 flex items-center flex-wrap gap-x-3 gap-y-1.5 text-xs text-secondary-500">
            <RatingStars value={software.rating} />
            <span className="flex items-center gap-1">
              <span className="flex items-center justify-center w-4 h-4"><i className="ri-download-2-line" aria-hidden="true"></i></span>
              {formatDownloads(software.downloads)}次
            </span>
            <span className="flex items-center gap-1">
              <span className="flex items-center justify-center w-4 h-4"><i className="ri-calendar-line" aria-hidden="true"></i></span>
              {software.updatedAt}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {software.platforms.map((p) => (
          <span
            key={p}
            className="inline-flex items-center gap-1 text-[11px] text-secondary-700 bg-secondary-100 rounded-md px-2 py-0.5"
          >
            <span className="flex items-center justify-center w-3.5 h-3.5">
              <i className={p === "ios" ? "ri-apple-fill" : "ri-android-line"} aria-hidden="true"></i>
            </span>
            {platformLabel(p)}
          </span>
        ))}
        {software.tags.slice(0, 3).map((tag) => (
          <span key={tag} className="text-[11px] text-secondary-600 bg-background-100 rounded-md px-2 py-0.5">
            {tag}
          </span>
        ))}
      </div>

      <div className="mt-3.5 hidden group-hover:flex items-center justify-between gap-3 pt-3 border-t border-background-200">
        <span className="text-xs text-secondary-500">软件大小 {software.size}</span>
        <a
          href={software.downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="relative z-20 inline-flex items-center gap-1 h-8 px-3 text-xs rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="flex items-center justify-center w-3.5 h-3.5"><i className="ri-download-cloud-line" aria-hidden="true"></i></span>
          前往下载
        </a>
      </div>
    </article>
  );
}